import {
  type Company,
  type CompanyImport,
  type CreateCompanyImportRequest,
  type ExternalCompany,
} from '~/common/types/company/company';
import { type CompanyResponse, type CompanyTeamResponse } from '~/backend/types/company';
import { type Obj, type PaginatedAutocomplete } from '~/common/types';
import { type CompanyWebData } from '~/common/types/companyWeb/companyWeb';
import { type ExternalDataProvider } from '~/common/enums';
import { type RelationBuildingInfo } from '~/common/types/building';
import { type SendMessageRequest } from '~/common/types/email/emailMessage';

import config from '@/config/config';

import createRestClient from '../restClient';
import { objectToFormData } from '@/shared/utils/helpers';

const client = createRestClient(config.baseURL);

const getExternalCompanies = (
  provider: ExternalDataProvider,
  page: number,
  filter: string | null,
): Promise<ExternalCompany[]> =>
  client.get('external/companies', { params: { filter, page, provider } });

// @ts-ignore
const getCompaniesAutoComplete = (params): Promise<PaginatedAutocomplete> =>
  client.get('/company/autocomplete', { params });

const getContractorsAutocomplete = (params: Obj): Promise<PaginatedAutocomplete> =>
  client.get('/company/autocomplete', { params: { ...params, isContractor: true } });

const restClient = createRestClient();

// @ts-ignore
const linkContactToCompany = (data) => restClient.post('company/contact', data);

// @ts-ignore
const unlinkContactFromCompany = (data) => restClient.delete('company/contact', data);

// @ts-ignore
const createCompany = (company): Promise<CompanyResponse> => restClient.post('company', company);

// @ts-ignore
const addCompanyAddress = (companyAddress) => restClient.post('company/address', companyAddress);

const deleteCompany = (companyId: string) => restClient.delete(`company/${companyId}`);

// @ts-ignore
const deleteCompanyAddress = (companyAddress) =>
  restClient.delete('company/address', { data: companyAddress });

const getCompanies = (): Promise<Company[]> => restClient.get('company');

const getCompanyTeams = (): Promise<CompanyTeamResponse[]> => restClient.get('company/teams');

const getCompany = (companyId: string): Promise<Company> => restClient.get(`company/${companyId}`);

const getCompanyBuildingInfo = (id: string): Promise<RelationBuildingInfo[]> =>
  client.get(`/company/${id}/building-info`);

const getCompanyTags = () => restClient.get('company/tags');

const getCompanyPublicData = (countryCode: string, vatNumber: string) =>
  restClient.get('company/public.data', { params: { countryCode, vatNumber } });

// @ts-ignore
const linkTag = (tag) => restClient.put('company/tag.link', tag);

// @ts-ignore
const createTag = (tag) => restClient.post('company/tag', tag);

// @ts-ignore
const updateTag = (tag) => restClient.put('company/tag', tag);

const deleteTag = (id: string) => restClient.delete('company/tag', { data: { tagId: id } });

// @ts-ignore
const updateCompany = (company) => restClient.put('company', company);

// @ts-ignore
const unLinkTag = (tag) => restClient.put('company/tag.unlink', tag);

// @ts-ignore
const updateCompanyAddress = (companyAddress) => restClient.put('company/address', companyAddress);

const getCompanyWebDetails = (vat: string): Promise<CompanyWebData> =>
  restClient.get(`companyweb/details/${vat}`);

// @ts-ignore
const updateCompanyPermissions = (permission) => restClient.post(`company/permissions`, permission);

const getCompanyImports = (): Promise<CompanyImport[]> => client.get('/company/import/list');

const deleteCompanyImport = (id: string) => client.delete(`/company/import/${id}`);

const createCompanyImport = ({
  data,
  onProgress,
}: {
  data: CreateCompanyImportRequest;
  onProgress: CallableFunction;
}) =>
  client.postForm('/company/import', data, {
    onUploadProgress: (progressEvent) => {
      if (progressEvent && progressEvent.loaded && progressEvent.total) {
        const progress = (progressEvent.loaded / progressEvent.total) * 100;

        onProgress(Math.round(progress));
      }
    },
  });

const downloadCompanyImportFile: (companyImportId: string) => Promise<string> = (companyImportId) =>
  client.get(`/company/import/${companyImportId}/download`);

const postSendEmailMessage = ({
  data,
  companyId,
}: {
  data: SendMessageRequest;
  companyId: string;
}) =>
  restClient.post(`company/${companyId}/emails`, objectToFormData(data), {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

export default {
  addCompanyAddress,
  createCompany,
  createCompanyImport,
  createTag,
  deleteCompany,
  deleteCompanyAddress,
  deleteCompanyImport,
  deleteTag,
  downloadCompanyImportFile,
  getCompanies,
  getCompaniesAutoComplete,
  getCompany,
  getCompanyBuildingInfo,
  getCompanyImports,
  getCompanyPublicData,
  getCompanyTags,
  getCompanyTeams,
  getCompanyWebDetails,
  getContractorsAutocomplete,
  getExternalCompanies,
  linkContactToCompany,
  linkTag,
  postSendEmailMessage,
  unlinkContactFromCompany,
  unLinkTag,
  updateCompany,
  updateCompanyAddress,
  updateCompanyPermissions,
  updateTag,
};
